import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const WeekScheduleSummary = () => {
  const navigate = useNavigate();
  const [weekSchedule, setWeekSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeekSchedule();
  }, []);

const fetchWeekSchedule = async () => {
  try {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const startDateStr = `${year}-${month}-${day}`;
    
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 6);
    const endYear = endDate.getFullYear();
    const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
    const endDay = String(endDate.getDate()).padStart(2, '0');
    const endDateStr = `${endYear}-${endMonth}-${endDay}`;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/drivers/rides/calendar`,
      {
        params: {
          start_date: startDateStr,
          end_date: endDateStr
        }
      }
    );

    // Handle both old (array) and new (object with events + driver_timezone) response shapes
    const responseData = response.data;
    const events = Array.isArray(responseData) ? responseData : (responseData.events || []);
    const tz = responseData.driver_timezone || null;

    // Helper to get date/time parts in the driver's timezone
    const getPartsInTZ = (isoString) => {
      if (!tz) {
        const d = new Date(isoString);
        return {
          dateStr: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
          hours: d.getHours(),
          minutes: d.getMinutes()
        };
      }
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date(isoString));
      const get = (type) => parts.find(p => p.type === type)?.value;
      return {
        dateStr: `${get('year')}-${get('month')}-${get('day')}`,
        hours: parseInt(get('hour')),
        minutes: parseInt(get('minute'))
      };
    };

    const dayMap = {};

    // Initialize all days in the week
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      dayMap[dateStr] = {
        date: dateStr,
        events: [],
        summary: { status: 'unavailable', total_rides: 0 }
      };
    }

    // Group events by day in the driver's timezone
    events.forEach(event => {
      const startParts = getPartsInTZ(event.start);
      const endParts = getPartsInTZ(event.end);
      const dateStr = startParts.dateStr;

      if (dayMap[dateStr]) {
        dayMap[dateStr].events.push({
          type: event.type,
          start_time: `${String(startParts.hours).padStart(2, '0')}:${String(startParts.minutes).padStart(2, '0')}`,
          end_time: `${String(endParts.hours).padStart(2, '0')}:${String(endParts.minutes).padStart(2, '0')}`,
          title: event.title
        });
        
        // Count rides
        if (event.type === 'ride') {
          dayMap[dateStr].summary.total_rides++;
        }
        
        // Update status
        if (event.type === 'ride' || event.type === 'pre-buffer' || event.type === 'post-buffer') {
          dayMap[dateStr].summary.status = 'busy';
        } else if (dayMap[dateStr].summary.status !== 'busy' && event.type === 'available') {
          dayMap[dateStr].summary.status = 'available';
        }
      }
    });

    setWeekSchedule(Object.values(dayMap));
    setLoading(false);
  } catch (error) {
    console.error('Error fetching week schedule:', error);
    setLoading(false);
  }
};

  const getStatusBadge = (status) => {
    const badges = {
      busy: { bg: 'bg-green-100', text: 'text-green-800', label: 'Busy' },
      available: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Available' },
      unavailable: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Day Off' }
    };
    return badges[status] || badges.unavailable;
  };

  const getEventColor = (type) => {
    const colors = {
      'ride': 'bg-green-400',
      'pre-buffer': 'bg-yellow-300',
      'post-buffer': 'bg-green-600',
      'blackout': 'bg-purple-500',
      'available': 'bg-blue-300'
    };
    return colors[type] || 'bg-gray-300';
  };

    const formatDate = (dateStr) => {
    // Parse date string as local date, not UTC
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for accurate comparison
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
        return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
        return 'Tomorrow';
    }
    
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
    };

  const calculateTimelineSegments = (events) => {
    // Create 24-hour timeline segments based on events
    const segments = [];
    const dayMinutes = 24 * 60; // Total minutes in a day
    
    if (events.length === 0) {
      return [{ type: 'empty', percentage: 100 }];
    }

    // Convert time strings to minutes since midnight
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    // Sort events by start time
    const sortedEvents = [...events].sort((a, b) => 
      timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
    );

    let lastEndMinutes = 0;

    sortedEvents.forEach(event => {
      const startMinutes = timeToMinutes(event.start_time);
      const endMinutes = timeToMinutes(event.end_time);

      // Add gap if there's space before this event
      if (startMinutes > lastEndMinutes) {
        segments.push({
          type: 'empty',
          percentage: ((startMinutes - lastEndMinutes) / dayMinutes) * 100
        });
      }

      // Add the event segment
      segments.push({
        type: event.type,
        percentage: ((endMinutes - startMinutes) / dayMinutes) * 100,
        title: event.title
      });

      lastEndMinutes = Math.max(lastEndMinutes, endMinutes);
    });

    // Fill remaining time with empty
    if (lastEndMinutes < dayMinutes) {
      segments.push({
        type: 'empty',
        percentage: ((dayMinutes - lastEndMinutes) / dayMinutes) * 100
      });
    }

    return segments;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Your Week at a Glance</h2>
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">Loading schedule...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg sm:text-xl font-bold">Your Week at a Glance</h2>
        <button
          onClick={() => navigate('/driver/calendar')}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          View Full Calendar →
        </button>
      </div>

      <div className="flex overflow-x-auto snap-x snap-mandatory gap-2 pb-2 sm:grid sm:grid-cols-7 sm:gap-3 sm:overflow-visible sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
        {weekSchedule.map((day) => {
          const badge = getStatusBadge(day.summary.status);
          const segments = calculateTimelineSegments(day.events);

          return (
            <div
              key={day.date}
              onClick={() => navigate(`/driver/calendar?date=${day.date}`)}
              className="flex-shrink-0 w-[calc(33.333%-0.5rem)] min-w-[90px] snap-start sm:w-auto sm:min-w-0 cursor-pointer hover:shadow-lg transition-shadow border rounded-lg p-1.5 sm:p-3 bg-gray-50"
            >
            {/* Date Header */}
            <div className="text-center mb-1 sm:mb-2">
            <div className="text-[10px] sm:text-xs font-medium text-gray-600 truncate">
                {formatDate(day.date)}
            </div>
            <div className="text-base sm:text-lg font-bold text-gray-900">
                {day.date.split('-')[2]}
            </div>
            </div>

              {/* Timeline Visualization */}
              <div className="mb-1.5 sm:mb-3 h-16 sm:h-24 flex flex-col gap-0.5 rounded overflow-hidden">
                {segments.map((segment, idx) => (
                  <div
                    key={idx}
                    className={`${getEventColor(segment.type)} transition-all`}
                    style={{ height: `${segment.percentage}%` }}
                    title={segment.title}
                  />
                ))}
              </div>

              {/* Status Badge */}
              <div className={`text-center text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded ${badge.bg} ${badge.text} font-medium truncate`}>
                {day.summary.total_rides > 0
                  ? `${day.summary.total_rides} ride${day.summary.total_rides > 1 ? 's' : ''}`
                  : badge.label
                }
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-[10px] sm:text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-400 rounded"></div>
          <span>Rides</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-600 rounded"></div>
          <span>Buffer</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-purple-500 rounded"></div>
          <span>Blackout</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-300 rounded"></div>
          <span>Available</span>
        </div>
      </div>
    </div>
  );
};

export default WeekScheduleSummary;